import styled, { DefaultTheme } from 'styled-components'
import { space } from 'styled-system'
import { TextVariant, getTextStylesByVariant } from '../Text'
import { ButtonProps, ButtonThemeVariant, variants } from './types'

type ThemedProps = {
  theme: DefaultTheme
} & ButtonProps

const getDisabledStyles = ({ isLoading, theme, variant = variants.PRIMARY, disabled }: ThemedProps) => {
  const variantTheme = theme.button[variant]

  if (isLoading === true) {
    return `
      &:disabled,
      &.button--disabled {
        cursor: not-allowed;
      }
    `
  }

  if (disabled) {
    return `
    &:disabled,
    &::before,
    &::after,
    &.button--disabled {
      background: ${variantTheme.backgroundDisabled};
      color: ${variantTheme.colorDisabled};
      border: ${variantTheme.borderDisabled};
      cursor: not-allowed;
    }
  `
  }
  return ''
}

const removePointerEvents = ({ disabled, as }: ThemedProps) => {
  if (disabled && as && as !== 'button') {
    return `
      pointer-events: none;
    `
  }

  return ''
}

const getButtonVariantProp =
  (prop: keyof ButtonThemeVariant) =>
  ({ theme, variant = variants.PRIMARY }: ThemedProps) => {
    return theme.button[variant]?.[prop]
  }

const getFontStyles = ({ size }: ThemedProps) => {
  switch (size) {
    default:
      return { ...getTextStylesByVariant(TextVariant.BUTTON) }
  }
}

const StyledButton = styled.button.attrs<ButtonProps>((props) => ({
  type: props.type ?? 'button',
}))<ButtonProps>`
  position: relative;
  ${getFontStyles}
  align-items: center;
  border: 0;
  background: ${getButtonVariantProp('background')};
  border-radius: ${({ variant }) => (variant === variants.SECONDARY ? '8px' : '35px')};
  color: ${({ white }) => (white ? 'white' : getButtonVariantProp('color'))};
  cursor: pointer;
  display: inline-flex;
  /* max-content instead of auto for Safari fix */
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'max-content')};
  height: ${({ size }) => (size === 'sm' ? '40px' : '56px')};
  line-height: 1;
  letter-spacing: 0.03em;
  justify-content: center;
  outline: 0;
  padding: ${({ size }) => (size === 'sm' ? '0 16px' : '16px 24px')};
  opacity: ${({ isLoading }) => (isLoading ? 0.5 : 1)};
  border: ${getButtonVariantProp('border')};
  text-decoration: ${({ variant }) => (variant === variants.TEXT ? 'underline' : 'none')};
  z-index: 1;
  transition: all 0.3s ease-in-out;
  :hover:not(:disabled):not(.button--disabled) {
    background: ${getButtonVariantProp('backgroundHover')};
    color: ${getButtonVariantProp('colorHover')};
    border: ${getButtonVariantProp('borderHover')};
  }
  ${getDisabledStyles}
  ${removePointerEvents}
  ${space}
`

export default StyledButton
